<template>
    <common-modal width="huge" @close="emitEvent({ name: 'close' })">
        <common-content-spacer height="medium">
            <div class="user">
                <common-user-avatar
                    :shouldLeadToProfile="true"
                    :withBorder="true"
                    :user="clientRecordsProp"
                    size="medium-small"
                ></common-user-avatar>
                <p>{{ clientRecordsProp?.firstName }} {{ clientRecordsProp?.lastName }}</p>
            </div>
            <div v-if="!clientRecord?.companyClientRecords?.length">
                <span>{{ $t('common.microcopy.no_results') }}</span>
            </div>
            <common-loader v-else :isProcessing="isLoading"></common-loader>
            <div v-if="!isLoading" height="medium" class="record">
                <div v-for="(item, index) in clientRecord?.companyClientRecords" :key="index" class="record--item">
                    <select-input
                        class="select-wrapper"
                        :label="$t('common.microcopy.key')"
                        :placeholder="$t('common.microcopy.key')"
                        :options="keysOptions"
                        :isRequired="true"
                        :prefillVal="item?.key || ''"
                        :valuePath="index"
                        :isSearchable="true"
                        @change="inputKeyChanged(index, $event)"
                        @search-term-changed="searchTermChanged"
                    ></select-input>
                    <textarea-input
                        :label="$t('common.microcopy.value')"
                        :placeholder="$t('common.microcopy.value')"
                        :isRequired="true"
                        type="text"
                        :maxlength="1000"
                        :prefillVal="item?.description || ''"
                        @change="inputDescriptionChanged(index, $event)"
                    ></textarea-input>
                    <div class="record--item--actions">
                        <div class="record--item--actions--protected">
                            <span>{{ $t('common.microcopy.protected') }}</span>
                            <common-icon
                                class="record--item--actions--protected"
                                :name="item.protected ? 'checkbox-on' : 'checkbox-off'"
                                size="large"
                                @click="changeProtectedRecord(item)"
                            ></common-icon>
                        </div>
                        <common-buttons>
                            <common-button
                                :isDisabled="isEditSaveDisabled(item)"
                                type="secondary"
                                size="micro"
                                :label="item.id ? $t('common.microcopy.edit') : $t('common.microcopy.save')"
                                @click="item.id ? editRecord(item) : saveNewRecord(item)"
                            ></common-button>
                            <common-button
                                v-if="item.id"
                                type="tertiary"
                                size="micro"
                                :label="$t('common.microcopy.remove')"
                                @click="removeRecord(item)"
                            ></common-button>
                        </common-buttons>
                    </div>
                </div>
            </div>
            <common-buttons>
                <common-button
                    :isDisabled="isAddNewRecordDisabled"
                    type="primary"
                    size="medium"
                    :label="$t('common.microcopy.add')"
                    @click="addClientRecord()"
                ></common-button>
            </common-buttons>
        </common-content-spacer>
    </common-modal>
</template>

<script>
    import TextareaInput from 'components/form-inputs/TextareaInput'
    import ClientAPI from 'api/ClientAPI'
    import SelectInput from 'components/form-inputs/SelectInput'
    import isEqual from 'lodash.isequal'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        components: {
            TextareaInput,
            SelectInput,
        },
        props: {
            clientId: { type: Number, required: true },
            companyId: { type: Number, required: true },
            clientRecordsProp: { type: Object, required: true },
        },

        data() {
            return {
                clientRecord: {},
                keysOptions: [],
                isLoading: true,
                originalRecords: [],
            }
        },

        computed: {
            isAddNewRecordDisabled() {
                return (
                    this.clientRecord?.companyClientRecords?.length &&
                    !this.clientRecord?.companyClientRecords[this.clientRecord?.companyClientRecords.length - 1]?.id
                )
            },
        },

        methods: {
            getClient() {
                ClientAPI.getCompanyClientById(this.clientId, this.companyId).then((response) => {
                    this.clientRecord = response
                    this.originalRecords = cloneDeep(this.clientRecord.companyClientRecords)
                })
            },

            isEditSaveDisabled(item) {
                return (
                    !item?.key.length ||
                    !item?.description.length ||
                    isEqual(
                        item,
                        this.originalRecords?.find((record) => record.id === item.id),
                    )
                )
            },

            changeProtectedRecord(item) {
                item.protected = !item.protected
            },

            addClientRecord() {
                if (!this.clientRecord.companyClientRecords?.length) {
                    this.clientRecord = {
                        companyClientRecords: [
                            {
                                description: '',
                                key: '',
                            },
                        ],
                    }
                } else {
                    this.clientRecord?.companyClientRecords.push({
                        description: '',
                        key: '',
                    })
                }
            },

            updateOriginalRecords() {
                this.originalRecords = cloneDeep(this.clientRecord.companyClientRecords)
            },

            editRecord(item) {
                const request = {
                    key: item.key,
                    description: item.description,
                    protected: item.protected,
                }

                ClientAPI.editClientRecord(request, item.id, this.companyId)
                    .then(() => {
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.updateOriginalRecords()
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            },

            searchTermChanged(payload) {
                this.clientRecord.companyClientRecords[payload.index].key = payload.searchTerm
            },

            saveNewRecord(item) {
                const request = {
                    key: item.key,
                    description: item.description,
                    userId: this.clientId,
                    protected: item.protected,
                }
                ClientAPI.addNewClientRecord(request, this.companyId)
                    .then((response) => {
                        item.id = response.id
                        this.updateOriginalRecords()
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.getKeys()
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            },

            removeRecord(item) {
                ClientAPI.deleteClientRecord(item.id, this.companyId)
                    .then(() => {
                        const index = this.clientRecord?.companyClientRecords.findIndex((el) => el.id === item.id)
                        if (index !== -1) {
                            this.clientRecord?.companyClientRecords.splice(index, 1)
                            this.$toast.success(this.$t('common.microcopy.success'))
                        }
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            },

            inputKeyChanged(index, value) {
                if (value.value || value.value?.length === 0) {
                    this.clientRecord.companyClientRecords[index].key = value.value.toString()
                    this.isEditSaveDisabled(this.clientRecord.companyClientRecords[index])
                }
            },

            inputDescriptionChanged(index, value) {
                if (value.value || value.value?.length === 0) {
                    this.clientRecord.companyClientRecords[index].description = value.value
                    this.isEditSaveDisabled(this.clientRecord.companyClientRecords[index])
                }
            },

            getKeys() {
                this.isLoading = true
                ClientAPI.getKeys(this.companyId)
                    .then((response) => {
                        this.keysOptions = response.map((item) => {
                            return {
                                value: item.toString(),
                                label: item.toString(),
                            }
                        })
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
        },

        mounted() {
            this.getClient()
            this.getKeys()
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .user {
        display: flex;
        align-items: center;
        .avatar {
            margin-right: 10px;
        }
    }

    .record {
        @include breakpoint(smartphone-large) {
            height: auto;
            max-height: unset;
        }
        max-height: 500px;
        overflow-y: auto;
        padding-right: 10px;
        overflow-x: hidden;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;

        &--item {
            @include breakpoint(smartphone-large) {
                flex-wrap: wrap;
                width: 100%;
            }
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            width: calc(50% - 40px);
            border: 1px solid #d3e8f2;
            border-radius: 5px;
            padding: 10px;
            margin: 0 20px 20px 20px;

            .form-input-text {
                @include breakpoint(smartphone-large) {
                    width: 100%;
                }
                width: 100%;

                textarea {
                    min-height: 100px !important;
                    padding: 10px;
                }
            }

            .select-wrapper {
                @include breakpoint(smartphone-large) {
                    width: 100% !important;
                }
                width: 100%;
            }

            &--actions {
                @include breakpoint(smartphone-large) {
                    min-width: 100%;
                    margin-top: 10px;
                }

                &--protected {
                    width: 100%;
                    margin-bottom: 15px;
                }

                width: 100%;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;

                .icon {
                    cursor: pointer;
                    margin: 0 10px;
                }

                &--protected {
                    cursor: pointer;
                    color: $color--accent-1;
                }

                &--approve {
                    color: $color--accent-1;
                }

                &--delete {
                    color: $color--accent-4;
                }
            }
        }
    }
</style>
